import { Components, Theme } from '@mui/material'
import { DISABLED_OPACITY } from './disabledOpacity'
import { insetBorder } from './insetBorder'

const loadingIndicatorSelector = '&.MuiButton-loadingPositionCenter .MuiButton-loadingIndicator'

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    outlined: false
  }
}

export const primaryContainedButtonStyle = (theme: Theme) => ({
  backgroundColor: theme.palette.bg.brand.default,
  color: theme.palette.text.brand.onBrand,
  '&:hover': {
    backgroundColor: theme.palette.bg.brand.hover,
  },
  [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
    backgroundColor: theme.palette.bg.brand.default,
    color: theme.palette.text.brand.onBrand,
  },
})

// The border is an inset shadow rather than a real border so that it doesn't add to the box —
// a secondary button has to be exactly as tall as every other button of the same size.
export const secondaryContainedButtonStyle = (theme: Theme) => ({
  backgroundColor: theme.palette.bg.surface.elevated,
  color: theme.palette.text.muted,
  boxShadow: [
    insetBorder({ width: theme.strokeWidth.default, color: theme.palette.border.subtle }),
    theme.customShadows.buttonDrop,
  ].join(', '),
  // `disableElevation` zeroes box-shadow on :focus-visible and :active, so both states have to
  // restate the border or it vanishes while the button is focused or held down.
  '&.Mui-focusVisible': {
    boxShadow: insetBorder({ width: theme.strokeWidth.default, color: theme.palette.border.subtle }),
  },
  '&:hover': {
    backgroundColor: theme.palette.bg.surface.elevated,
    color: theme.palette.text.default,
    boxShadow: [
      insetBorder({ width: theme.strokeWidth.default, color: theme.palette.border.strong }),
      theme.customShadows.buttonInner,
      theme.customShadows.buttonDrop,
    ].join(', '),
  },
  '&:active': {
    boxShadow: insetBorder({ width: theme.strokeWidth.default, color: theme.palette.border.strong }),
  },
  [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
    backgroundColor: theme.palette.bg.surface.elevated,
    color: theme.palette.text.default,
    boxShadow: [
      insetBorder({ width: theme.strokeWidth.default, color: theme.palette.border.subtle }),
      theme.customShadows.buttonInner,
      theme.customShadows.buttonDrop,
    ].join(', '),
  },
})

export const MuiButton: Components<Omit<Theme, 'components'>>['MuiButton'] = {
  defaultProps: {
    disableRipple: true,
    disableElevation: true,
    loadingPosition: 'center',
  },
  styleOverrides: {
    root: ({ theme }) => ({
      '&.Mui-disabled': {
        opacity: DISABLED_OPACITY,
      },
      '&.Mui-disabled.MuiButton-loading.MuiButton-loadingPositionCenter': {
        color: 'transparent',
      },
      ...theme.typography['body-medium'],
      textTransform: 'none',
    }),
    startIcon: ({ theme }) => ({
      marginRight: theme.spacing(1.5),
      marginLeft: theme.spacing(-0.5),
      '& > *:nth-of-type(1)': {
        fontSize: 18,
      },
      '& > svg:not(.MuiSvgIcon-root):nth-of-type(1)': {
        width: '18px',
        height: '18px',
      },
    }),
    endIcon: ({ theme }) => ({
      marginLeft: theme.spacing(1.5),
      marginRight: theme.spacing(-0.5),
      '& > *:nth-of-type(1)': {
        fontSize: 18,
      },
      '& > svg:not(.MuiSvgIcon-root):nth-of-type(1)': {
        width: '18px',
        height: '18px',
      },
    }),
  },
  variants: [
    {
      props: { variant: 'text' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.invisible,
      }),
    },
    // Colour
    {
      props: { color: 'primary', variant: 'contained' },
      style: ({ theme }) => primaryContainedButtonStyle(theme),
    },
    {
      props: { color: 'primary', variant: 'text' },
      style: ({ theme }) => ({
        color: theme.palette.text.brand.default,
        '&:hover': {
          backgroundColor: theme.palette.bg.brand.muted,
          color: theme.palette.text.brand.onBrandMuted,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          color: theme.palette.text.brand.default,
        },
      }),
    },
    {
      props: { color: 'secondary', variant: 'contained' },
      style: ({ theme }) => secondaryContainedButtonStyle(theme),
    },
    {
      props: { color: 'secondary', variant: 'text' },
      style: ({ theme }) => ({
        color: theme.palette.text.muted,
        '&:hover': {
          color: theme.palette.text.default,
          backgroundColor: theme.palette.bg.interactive.hover,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          color: theme.palette.text.default,
        },
      }),
    },
    {
      props: { color: 'error', variant: 'contained' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.danger.default,
        color: theme.palette.text.danger.onDanger,
        '&:hover': {
          backgroundColor: theme.palette.bg.danger.hover,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          backgroundColor: theme.palette.bg.danger.default,
          color: theme.palette.text.danger.onDanger,
        },
      }),
    },
    {
      props: { color: 'error', variant: 'text' },
      style: ({ theme }) => ({
        color: theme.palette.text.danger.default,
        '&:hover': {
          color: theme.palette.text.danger.onDangerMuted,
          backgroundColor: theme.palette.bg.danger.muted,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          color: theme.palette.text.danger.onDangerMuted,
        },
      }),
    },
    // Size. Horizontal padding on large/medium is the design's own compound value — spacing plus a
    // further 0.5-step around the label text that isn't itself a wrapped element — expressed as
    // that sum via calc() rather than a single non-standard spacing factor.
    {
      props: { size: 'large' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['radius-lg'],
        padding: `${theme.spacing(2)} calc(${theme.spacing(4)} + ${theme.spacing(0.5)})`,
      }),
    },
    {
      props: { size: 'medium' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['radius-lg'],
        padding: `${theme.spacing(1.5)} calc(${theme.spacing(3)} + 2px)`,
      }),
    },
    {
      props: { size: 'small' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['radius-md'],
        padding: theme.spacing(0.5, 2),
      }),
    },
  ],
}
